import { api } from './api';

// Varianten-Gruppen — eine Sammlung benannter Alternativen ("1 Euro",
// "2 Euro", ...) für dasselbe Element. Jede Variante zeigt auf bis zu zwei
// Media (landscape + portrait), sodass eine Variante selbst wiederum ein
// Hoch+Quer-Paar sein kann. Im Stream wählt der Nutzer aus dem Dropdown.
export const variantGroupsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getVariantGroups: build.query({
      query: () => ({ url: 'variant-groups', method: 'POST', body: JSON.stringify({}) }),
      transformResponse: (r) => r.data || [],
      providesTags: (result) =>
        result
          ? [
            ...result.map((g) => ({ type: 'VariantGroups', id: g.model.ID })),
            { type: 'VariantGroups', id: 'LIST' },
          ]
          : [{ type: 'VariantGroups', id: 'LIST' }],
    }),

    getVariantGroup: build.query({
      query: (id) => ({ url: `variant-group/${id}`, method: 'GET' }),
      // Keep stream-usage info on the group object as frontend-only fields
      // so the form's "Verwendung" tab can render without a second query.
      transformResponse: (r) => ({
        ...r.data,
        _streams: r.streams || [],
        _stream_count: r.stream_counts || 0,
      }),
      providesTags: (_r, _e, id) => [{ type: 'VariantGroups', id }],
    }),

    createVariantGroup: build.mutation({
      // Body shape:
      //   { name, description, variants: [{name, landscape_media_id?, portrait_media_id?}],
      //     source_media_id?: number }
      // source_media_id makes the form-from-an-element flow trivial: the
      // backend auto-attaches the source media to the first variant on the
      // matching orientation slot and sets that variant as the group default.
      query: (body) => ({
        url: 'variant-group',
        method: 'POST',
        body: JSON.stringify(body),
      }),
      invalidatesTags: (_r, _e, body) => {
        const tags = [{ type: 'VariantGroups', id: 'LIST' }];
        if (body?.source_media_id) {
          tags.push({ type: 'Elements', id: body.source_media_id });
        }
        return tags;
      },
    }),

    updateVariantGroup: build.mutation({
      query: ({ id, ...body }) => ({
        url: `variant-group/${id}`,
        method: 'PUT',
        body: JSON.stringify(body),
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'VariantGroups', id },
        { type: 'VariantGroups', id: 'LIST' },
      ],
    }),

    deleteVariantGroup: build.mutation({
      query: (id) => ({ url: `variant-group/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'VariantGroups', id },
        { type: 'VariantGroups', id: 'LIST' },
        { type: 'Elements', id: 'LIST' },
        { type: 'Elements', id: 'NEWEST' },
        { type: 'Elements', id: 'HIGHLIGHTED' },
        { type: 'Streams' },
      ],
    }),

    syncVariantGroupSettings: build.mutation({
      // Body shape: { name, description, settings: {default_duration,
      // public, tags, groups, remove_after, remove_date, highlight,
      // highlight_start, highlight_end} }. Backend updates the group's own
      // fields and propagates the shared settings to every member media
      // and their pair partners.
      query: ({ id, ...body }) => ({
        url: `variant-group/${id}/sync-settings`,
        method: 'POST',
        body: JSON.stringify(body),
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'VariantGroups', id },
        { type: 'VariantGroups', id: 'LIST' },
        // Member media changed too — refresh the gallery and any open
        // element forms so settings are picked up.
        { type: 'Elements', id: 'LIST' },
      ],
    }),

    setVariantGroupDefault: build.mutation({
      query: ({ id, variantId }) => ({
        url: `variant-group/${id}/default`,
        method: 'POST',
        body: JSON.stringify({ variant_id: variantId }),
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'VariantGroups', id }],
    }),

    addVariant: build.mutation({
      query: ({ id, name, landscapeMediaId = null, portraitMediaId = null }) => ({
        url: `variant-group/${id}/variants`,
        method: 'POST',
        body: JSON.stringify({
          name,
          landscape_media_id: landscapeMediaId,
          portrait_media_id: portraitMediaId,
        }),
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'VariantGroups', id },
        { type: 'VariantGroups', id: 'LIST' },
      ],
    }),

    updateVariant: build.mutation({
      query: ({ id, name, position = 0, landscapeMediaId = null, portraitMediaId = null, groupId }) => ({
        url: `variant/${id}`,
        method: 'PUT',
        body: JSON.stringify({
          name,
          position,
          landscape_media_id: landscapeMediaId,
          portrait_media_id: portraitMediaId,
        }),
      }),
      invalidatesTags: (_r, _e, { groupId }) => groupId
        ? [{ type: 'VariantGroups', id: groupId }, { type: 'VariantGroups', id: 'LIST' }]
        : [{ type: 'VariantGroups', id: 'LIST' }],
    }),

    removeVariant: build.mutation({
      query: ({ id }) => ({
        url: `variant/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { groupId }) => groupId
        ? [
          { type: 'VariantGroups', id: groupId },
          { type: 'VariantGroups', id: 'LIST' },
          { type: 'Streams' },
        ]
        : [{ type: 'VariantGroups', id: 'LIST' }, { type: 'Streams' }],
    }),
  }),
});

export const {
  useGetVariantGroupsQuery,
  useLazyGetVariantGroupsQuery,
  useGetVariantGroupQuery,
  useLazyGetVariantGroupQuery,
  useCreateVariantGroupMutation,
  useUpdateVariantGroupMutation,
  useDeleteVariantGroupMutation,
  useSyncVariantGroupSettingsMutation,
  useSetVariantGroupDefaultMutation,
  useAddVariantMutation,
  useUpdateVariantMutation,
  useRemoveVariantMutation,
} = variantGroupsApi;
